import CloseIcon from '@mui/icons-material/Close'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import { Button } from '@mui/material'
import React from 'react'
import Drawer from '../../../components/Drawer'
import GrantContent from '../../../components/GrantContent'
import * as Permisos from '../../../utils/Permisos'
import { useTaskDetailDrawerModel } from './task-detail-drawer-model'
import type { TaskDetailDrawerProps } from './task-detail-drawer-model'

export type { TaskDetailDrawerProps } from './task-detail-drawer-model'

const actionClass = 'inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border border-k-line-strong bg-transparent px-3 text-[13px] font-bold text-k-ink no-underline hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'

const Field = ({ label, children, wide }: { label: string; children?: React.ReactNode; wide?: boolean }) => (
    <div className={wide ? 'sm:col-span-2' : ''}>
        <dt className="mb-0.5 text-xs text-k-muted">{label}</dt>
        <dd className="m-0 whitespace-pre-line break-words text-sm font-semibold text-k-ink">{children || '—'}</dd>
    </div>
)

const TaskDetailDrawer = (props: TaskDetailDrawerProps) => {
    const { tareaId, onClose, onEdit } = props
    const model = useTaskDetailDrawerModel(props)
    const { tarea, loading, TypeIcon } = model

    return (
        <Drawer open={Boolean(tareaId)} onClose={onClose} bare width={640}>
            <div className="flex h-full min-h-0 flex-col" role="dialog" aria-modal="true" aria-label={tarea?.nombre ? `Detalle de ${tarea.nombre}` : 'Detalle de la tarea'}>
                <div className="flex shrink-0 flex-col gap-4 border-b border-k-line px-6 pb-4 pt-5">
                    <div className="flex items-start gap-3.5">
                        <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] ${model.closed ? 'bg-k-ok-soft text-k-ok' : 'bg-tone-blue text-tone-blue-ink'}`}>
                            {model.closed ? <TaskAltIcon style={{ fontSize: 28 }} /> : <TypeIcon style={{ fontSize: 28 }} />}
                        </span>
                        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                            <h2 className={`m-0 text-[21px] font-extrabold tracking-[-0.02em] ${model.closed ? 'text-k-muted line-through' : 'text-k-ink'}`}>{tarea?.nombre || 'Detalle de la tarea'}</h2>
                            {tarea && !loading && (
                                <div className="flex flex-wrap gap-1.5">
                                    <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${model.closed ? 'bg-k-ok-soft text-k-ok' : 'bg-k-accent-soft text-k-accent-ink'}`}>{model.status}</span>
                                    <span className={`inline-flex items-center gap-1 rounded-full px-[9px] py-[3px] text-xs font-bold ${model.prioridadClass}`}>
                                        <FlagOutlinedIcon style={{ fontSize: 13 }} />{model.prioridad}
                                    </span>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-k-line-soft px-[9px] py-[3px] text-xs font-bold text-k-ink-2">
                                        <TypeIcon style={{ fontSize: 13 }} />{model.tipo}
                                    </span>
                                </div>
                            )}
                        </div>
                        <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-k-line bg-transparent text-k-ink hover:bg-k-hover" onClick={onClose}>
                            <CloseIcon style={{ fontSize: 18 }} />
                        </button>
                    </div>

                    {tarea && !loading && (
                        <GrantContent permisos={[Permisos.TAREAS_W]}>
                            <div className="flex flex-wrap gap-2">
                                <button type="button" className={actionClass} onClick={model.toggleStatus} disabled={model.saving}>
                                    {model.closed ? <LockOpenOutlinedIcon style={{ fontSize: 15 }} /> : <TaskAltIcon style={{ fontSize: 15 }} />}
                                    {model.saving ? 'Guardando…' : model.closed ? 'Reabrir tarea' : 'Marcar como cerrada'}
                                </button>
                                <button type="button" className={actionClass} onClick={() => onEdit?.(tarea.id)}>
                                    <EditOutlinedIcon style={{ fontSize: 15 }} />Editar
                                </button>
                            </div>
                        </GrantContent>
                    )}
                </div>

                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-k-bg px-6 py-5">
                    {loading && (
                        <div className="flex flex-col items-center justify-center gap-4 py-12">
                            <RefreshIcon className="animate-spin text-k-primary" style={{ fontSize: 40 }} />
                            <p className="text-sm text-k-muted">Cargando detalle...</p>
                        </div>
                    )}

                    {model.error && !loading && (
                        <div className="rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                            <div className="flex items-center gap-2">
                                <ErrorOutlineIcon className="text-k-danger" style={{ fontSize: 20 }} />
                                <p className="text-sm text-k-danger-ink">{model.error}</p>
                            </div>
                            <Button className="mt-3" variant="contained" onClick={model.reload}>Reintentar</Button>
                        </div>
                    )}

                    {tarea && !loading && (
                        <section className="flex flex-col gap-3 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
                            <h3 className="m-0 text-[13px] font-extrabold uppercase tracking-[0.06em] text-k-muted">Datos de la tarea</h3>
                            <dl className="m-0 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">
                                <Field label="Descripción" wide>{tarea.descripcion}</Field>
                                <Field label="Vencimiento">
                                    <span className="flex flex-col">
                                        <span>{model.due.label}</span>
                                        {model.due.note && <span className={`text-xs ${model.due.noteClass}`}>{model.due.note}</span>}
                                    </span>
                                </Field>
                                <Field label="Vendedor">{model.vendedor || 'Sin vendedor'}</Field>
                                <Field label="Fecha de creación">{model.creacion}</Field>
                                <Field label="Fecha de cierre">{model.cierre}</Field>
                            </dl>
                        </section>
                    )}
                </div>
            </div>
        </Drawer>
    )
}

export default TaskDetailDrawer
